import { useContext, useEffect, useState } from 'react'
import { Box, styled } from '@mui/material'
import { ConferenceContext } from 'contexts/conference'
import { ConversationType, MessageType } from 'chat'
import { MessagePopup } from 'components/chat'
import { Selfie } from 'components/participants'
import { SidebarComponentProps, SidebarProps } from 'components/sidebar'
import { ButtonWrapper, ChatButton } from 'components/buttons'
import { getDisplayName } from 'utils/get-display-name'
import { VideoBackgroundIconButton } from 'components/icon-buttons'
import ChatPortal from 'components/chatBot/ChatPortal'
import { VBDialog } from 'pages/Diagnostics/components/virtual-background/VirtualBackground'
import { MediaDevice } from '@zoom/videosdk'
import { useMediaDevices } from 'pages/Diagnostics/useMediaDevices'

const Wrapper = styled('div')(({ theme }) => ({
    padding: `${theme.spacing(16)} var(--page-side-margin) var(--page-v-margin)`,
    boxSizing: 'content-box',
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        padding: `${theme.spacing(20)} var(--page-side-margin) var(--page-v-margin)`,
        flexDirection: 'column'
    }
}))
const LeftWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'nowrap',
    flex: 1,
    minWidth: theme.spacing(42),
    justifyContent: 'left',
    alignItems: 'flex-end',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        justifyContent: 'center'
    }
}))
const CenterWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1
})
const RightWrapper = styled('div')({
    display: 'flex',
    flex: 1
})
const SelfieWrapper = styled('div')({
    position: 'absolute',
    display: 'flex',
    alignItems: 'flex-end',
    overflow: 'hidden',
    boxSizing: 'content-box'
})
const VBButton = styled(Box)(({ theme }) => ({
    margin: `${theme.spacing(0, 2, 1, 1)} !important`
}))
const SelfieContainer = styled(Box)(({ theme }) => ({
    border: `${theme.spacing(1)} solid ${theme.palette.background?.default}`,
    borderRadius: theme.spacing(2.5),
    overflow: 'hidden'
}))

type ChooseBackgroundType = { active: boolean; url: string; change: string }

const BottomControls: React.FC<SidebarComponentProps> = ({ currentSidebar, setCurrentSidebar }) => {
    const conference = useContext(ConferenceContext)
    const sessionJoined = conference.status === 'session-joined'
    const [cameraId, setCameraId] = useState<string>('')
    const { devices: mediaDevices } = useMediaDevices()
    const [wasVideoInitiallyOn, setWasVideoInitiallyOn] = useState <boolean>(false)
    const [backgroundSelected, setBackgroundSelected] = useState<ChooseBackgroundType>({
        active: false,
        url: sessionStorage.getItem('studentSelectedBackground') ?? '',
        change: sessionStorage.getItem('studentSelectedBackground') ?? ''
    })

    const mediaDeviceFilter = (kind: string): MediaDeviceInfo[] => {
        return mediaDevices?.filter((device: MediaDeviceInfo) => device?.kind === kind && device?.deviceId !== '') || []
    }

    useEffect(() => {
        let videoDevicesList: MediaDevice[] = []
        videoDevicesList = mediaDeviceFilter('videoinput')
        setCameraId(videoDevicesList[0]?.deviceId)
    }, [conference.status === 'session-joined' && conference.videoBG])

    const toggleSidebar = (currentState: SidebarProps): void => {
        setCurrentSidebar(prevState => prevState.type === currentState.type ? { type: null } : currentState)
    }

    const [lastMessage, setLastMessage] = useState<{ from: string; text: string; conversation: ConversationType } | null>(null)
    const [notificationsCount, setNotificationsCount] = useState<number>(0)
    const onMessageClick = (): void => {
        setCurrentSidebar({ type: 'chat', conversation: lastMessage?.conversation })
        setLastMessage(null)
    }
    const onMessageCloseClick = (e: React.MouseEvent<HTMLElement>): void => {
        e.stopPropagation()
        setLastMessage(null)
    }
    const onChatClick = (): void => {
        toggleSidebar({ type: 'chat' })
        setLastMessage(null)
    }

    useEffect(() => {
        if (sessionJoined) {
            setNotificationsCount(conference.chatClient.getTotalNotifications())

            const notificationHandler = (): void => {
                setNotificationsCount(conference.chatClient.getTotalNotifications())
            }
            const messageHandler = (message: MessageType, conversation: ConversationType): void => {
                if (conversation.chatId !== 'EVERYONE' && !message.isOwnMessage) {
                    setLastMessage({
                        from: getDisplayName(conversation, 'teacher-name'),
                        text: message.text,
                        conversation
                    })
                }
            }

            conference.chatClient.addNotificationHandler(notificationHandler)
            conference.chatClient.addChatMessageHandler(messageHandler)

            return () => {
                conference.chatClient.removeNotificationHandler(notificationHandler)
                conference.chatClient.removeChatMessageHandler(messageHandler)
            }
        }
    }, [conference.status])

    const handleToggleVirtualBackground = (): void => {
        if (conference.status === 'session-joined') {
            const studentVB = sessionStorage.getItem('studentSelectedBackground') || ''
            const isBackgroundEmpty = conference.videoBG === ''
            void conference.rtcSession.setVideoBackground(isBackgroundEmpty ? studentVB : '')
            const isBgApplied = sessionStorage.getItem('toggleVirtualBackground') === 'true'
            sessionStorage.setItem('toggleVirtualBackground', `${!isBgApplied}`)
        }
    }

    const handleOpenVirtualBackground = (): void => {
        if (conference.status === 'session-joined') {
            const isVideoMuted = conference.isVideoMuted
            setWasVideoInitiallyOn(isVideoMuted)
            if (!isVideoMuted) {
                void conference.rtcSession.setVideoMuted(!isVideoMuted)
            }
        }
    }

    return (
        <Wrapper>
            <LeftWrapper>
                <SelfieWrapper>
                    <SelfieContainer>
                        <Selfie />
                    </SelfieContainer>
                    {conference.status === 'session-joined'
                        ? <VBButton>
                            <VideoBackgroundIconButton
                                active={conference.videoBG !== ''}
                                onClick={() => {
                                    if (window.growthbook.isOn('goc-insession-virtualbackground')) {
                                        handleOpenVirtualBackground()
                                        setBackgroundSelected({ ...backgroundSelected, active: true })
                                    } else {
                                        handleToggleVirtualBackground()
                                    }
                                }}
                            />
                        </VBButton>
                        : null}
                </SelfieWrapper>
            </LeftWrapper>
            <CenterWrapper>
                <ButtonWrapper>
                    <ChatButton id='student-chat-button'
                        active={currentSidebar.type === 'chat'}
                        onClick={onChatClick}
                        disabled={!sessionJoined}
                        notifications={notificationsCount}
                    />
                    {lastMessage && sessionJoined ? (
                        <MessagePopup lastMessage={lastMessage} onMessageClick={onMessageClick} onCloseClick={e => onMessageCloseClick(e)} />
                    ) : null}
                    {sessionJoined && <ChatPortal />}
                </ButtonWrapper>
            </CenterWrapper>
            <RightWrapper />
            { backgroundSelected.active && <VBDialog
                close={() => { }}
                selectedBG={backgroundSelected.change}
                setBackgroundSelected={setBackgroundSelected}
                selectedCameraId={cameraId}
                wasVideoInitiallyOn = {wasVideoInitiallyOn}
            /> }
        </Wrapper>
    )
}

export { BottomControls }
